import { Component } from '@theme/component';

const genericWords = new Set(['and', 'the', 'for', 'with', 'from', 'shop', 'gifts', 'gift', 'ricordo']);
const words = (value) => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
const meaningful = (value) => words(value).filter((word) => word.length >= 3 && !genericWords.has(word));

function productUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, location.origin);
    return url.origin === location.origin && !url.username && !url.password && /^\/(?:[^/]+\/)?products\/[^/]+$/.test(url.pathname) ? url : null;
  } catch { return null; }
}

function imageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, location.origin);
    const cdnPath = url.origin === location.origin ? url.pathname.startsWith('/cdn/shop/') : url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/');
    return url.protocol === 'https:' && !url.username && !url.password && cdnPath ? url : null;
  } catch { return null; }
}

class RicordoSearchSuggestions extends Component {
  requiredRefs = ['suggestionsList'];
  #controller;
  #timer;
  #key = '';
  #products;
  #alternative;
  // The native morph retains this node but may call updatedCallback on its detached source.
  #observer = new MutationObserver(() => this.#scheduleLoad());

  connectedCallback() {
    super.connectedCallback();
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-query', 'data-endpoint', 'hidden'] });
    this.#scheduleLoad();
  }
  updatedCallback() { super.updatedCallback(); this.#scheduleLoad(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#observer.disconnect(); this.#controller?.abort(); clearTimeout(this.#timer); }

  #scheduleLoad() {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      super.updatedCallback();
      this.#load();
    });
  }

  async #load() {
    if (!this.isConnected) return;
    const query = (this.dataset.query || '').trim();
    const key = `${this.dataset.endpoint}|${query}`;
    if (key === this.#key && this.#products !== undefined) { this.#render(this.#products); return; }
    if (key === this.#key && this.#controller && !this.#controller.signal.aborted) return;
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.#key = key;
    this.#products = undefined;
    this.#alternative = null;
    this.querySelector('.rc-search-suggestions__alternative')?.remove();
    this.hidden = true;
    this.refs.suggestionsList.replaceChildren();
    if (query.length < 1 || query.length > 200) { this.#products = []; return; }
    const controller = new AbortController();
    this.#controller = controller;
    const timer = setTimeout(() => controller.abort(), 6000);
    this.#timer = timer;
    try {
      await this.#prepareAlternative(query, key);
      const endpoint = new URL(this.dataset.endpoint, location.origin);
      if (endpoint.origin !== location.origin || !endpoint.pathname.endsWith('/search/suggest.json')) throw new Error('Invalid endpoint');
      let products = await this.#request(endpoint, query, false, controller.signal);

      if (!controller.signal.aborted && this.isConnected && this.#key === key) { this.#products = products; this.#render(products); }
    } catch {
      // Keep the honest native empty state when the API fails, times out or finds no safe related items.
      if (this.#key === key && this.isConnected) { this.#products = []; this.#render([]); }
    } finally { clearTimeout(timer); }
  }

  async #prepareAlternative(query, key) {
    try {
      const moduleUrl = new URL(this.dataset.intelligenceUrl, location.origin);
      if (moduleUrl.origin !== location.origin || !moduleUrl.pathname.endsWith('/assets/ricordo-search-suggestions.js') || !moduleUrl.searchParams.has('v')) return;
      const { planQuery } = await import(moduleUrl.href);
      if (!this.isConnected || this.#key !== key) return;
      const plan = planQuery(query, document.documentElement.lang);
      if (!plan.suggestedQuery || plan.ambiguity.length || plan.suggestedQuery === plan.original) return;
      const url = new URL(this.dataset.searchUrl, location.origin);
      if (url.origin !== location.origin || !url.pathname.endsWith('/search')) return;
      url.searchParams.set('q', plan.suggestedQuery);
      url.searchParams.set('type', 'product');
      this.#alternative = { url: url.href, label: `${this.dataset.labelSearch}: “${plan.suggestedQuery}”` };
    } catch { /* Native empty results stay usable if the optional planner is unavailable. */ }
  }

  async #request(endpoint, query, requireEveryTerm, signal) {
    const url = new URL(endpoint);
    url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '4', 'resources[limit_scope]': 'all', 'resources[options][unavailable_products]': 'hide' });
    const response = await fetch(url, { method: 'GET', credentials: 'same-origin', signal });
    if (!response.ok) throw new Error('Suggestions unavailable');
    const data = await response.json();
    const products = data?.resources?.results?.products;
    if (!Array.isArray(products)) throw new Error('Invalid suggestions response');
    const terms = meaningful(query), seen = new Set();
    return products.slice(0, 10).flatMap((product) => {
      if (product?.available !== true || !/^\d+$/.test(String(product.id)) || seen.has(String(product.id)) || typeof product.title !== 'string') return [];
      const title = product.title.trim(), href = productUrl(product.url), image = imageUrl(product.featured_image?.url || product.image);
      if (!title || title.length > 400 || !href || !image) return [];
      const titleWords = words(title);
      const matches = (term) => titleWords.some((word) => word === term || (term.length >= 4 && word.startsWith(term)));
      if (!terms.length || (requireEveryTerm ? !terms.every(matches) : !terms.some(matches))) return [];
      seen.add(String(product.id));
      return [{ id: String(product.id), title, href: href.href, image: image.href }];
    }).slice(0, 4);
  }

  #render(products) {
    if (!this.isConnected || !this.refs.suggestionsList) return;
    this.querySelector('.rc-search-suggestions__alternative')?.remove();
    if (this.#alternative) {
      const paragraph = document.createElement('p'), link = document.createElement('a');
      paragraph.className = 'rc-search-suggestions__alternative';
      link.href = this.#alternative.url;
      link.textContent = this.#alternative.label;
      paragraph.append(link);
      this.refs.suggestionsList.before(paragraph);
    }
    const fragment = document.createDocumentFragment();
    for (const product of products) {
      const item = document.createElement('li'), link = document.createElement('a'), image = document.createElement('img'), title = document.createElement('h3');
      item.dataset.productId = product.id;
      link.className = 'rc-search-suggestions__link rc-shop-card__control';
      link.href = product.href;
      image.src = product.image; image.alt = ''; image.width = 400; image.height = 400; image.loading = 'lazy'; image.decoding = 'async';
      title.textContent = product.title;
      link.append(image, title); item.append(link); fragment.append(item);
    }
    this.refs.suggestionsList.replaceChildren(fragment);
    const isEmpty = products.length === 0 && !this.#alternative;
    if (this.hidden !== isEmpty) this.hidden = isEmpty;
  }
}

if (!customElements.get('ricordo-search-suggestions')) customElements.define('ricordo-search-suggestions', RicordoSearchSuggestions);
